import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { cursoService } from '@/services/api';
import { Curso } from '@/types';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useQueryInvalidation } from '@/hooks/useQueryInvalidation';

const cursoSchema = z.object({
  nome: z.string().min(1, 'Nome é obrigatório'),
  habilitacao: z.string().optional(),
  nivel: z.string().optional(),
});

type CursoFormData = Omit<Curso, 'id'>;

export const NovoCursoPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditing = !!id;
  const { handleError, handleSuccess } = useErrorHandler();
  const { invalidateCursos } = useQueryInvalidation();
  const [loadError, setLoadError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<CursoFormData>({
    resolver: zodResolver(cursoSchema),
  });

  // Buscar dados do curso (se editando)
  const { data: cursoData, isLoading: loadingCurso, error: cursoError } = useQuery<Curso>({
    queryKey: ['curso', id],
    queryFn: () => cursoService.getById(Number(id)),
    enabled: isEditing,
  });

  useEffect(() => {
    if (cursoError) {
      const message = handleError(cursoError as any, 'Erro ao carregar curso');
      setLoadError(message);
    }
  }, [cursoError, handleError]);

  useEffect(() => {
    if (cursoData) {
      setLoadError(null);
    }
  }, [cursoData]);

  const createCursoMutation = useMutation({
    mutationFn: cursoService.create,
    onSuccess: () => {
      invalidateCursos();
      handleSuccess('Curso criado com sucesso!');
      navigate('/cursos');
    },
    onError: (error: any) => {
      handleError(error, 'Erro ao criar curso');
    },
  });

  const updateCursoMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: CursoFormData }) =>
      cursoService.update(id, data),
    onSuccess: () => {
      invalidateCursos(id);
      handleSuccess('Curso atualizado com sucesso!');
      navigate('/cursos');
    },
    onError: (error: any) => {
      handleError(error, 'Erro ao atualizar curso');
    },
  });

  // Preencher formulário com dados existentes (se editando)
  useEffect(() => {
    if (cursoData) {
      setValue('nome', cursoData.nome);
      setValue('habilitacao', cursoData.habilitacao || '');

      // Normalizar o nível para corresponder às opções do select
      const nivelNormalizado = cursoData.nivel
        ? cursoData.nivel.charAt(0).toUpperCase() + cursoData.nivel.slice(1).toLowerCase()
        : '';
      setValue('nivel', nivelNormalizado);
    }
  }, [cursoData, setValue]);

  const onSubmit = async (data: CursoFormData) => {
    try {
      setIsSubmitting(true);
      if (isEditing) {
        await updateCursoMutation.mutateAsync({ id: Number(id), data });
      } else {
        await createCursoMutation.mutateAsync(data);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state para edição
  if (isEditing && loadError) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Editar Curso</h1>
          <p className="mt-1 text-sm text-gray-500">Não foi possível carregar as informações do curso.</p>
        </div>
        <Card>
          <CardHeader>
            <CardTitle>Erro ao carregar dados</CardTitle>
            <CardDescription>{loadError}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/cursos')}>Voltar para lista</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isEditing && loadingCurso) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-2 text-gray-500">Carregando dados do curso...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditing ? 'Editar Curso' : 'Novo Curso'}
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          {isEditing
            ? 'Edite as informações do curso'
            : 'Cadastre um novo curso no sistema'
          }
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Dados do Curso</CardTitle>
            <CardDescription>
              Informações básicas do curso
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="nome">Nome do Curso *</Label>
                <Input
                  id="nome"
                  placeholder="Ex: Ciência da Computação"
                  {...register('nome')}
                  className={errors.nome ? 'border-error-500' : ''}
                />
                {errors.nome && (
                  <p className="mt-1 text-sm text-error-600">{errors.nome.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="nivel">Nível</Label>
                <select
                  id="nivel"
                  {...register('nivel')}
                  className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
                >
                  <option value="">Selecione o nível</option>
                  <option value="Médio">Médio</option>
                  <option value="Técnico">Técnico</option>
                  <option value="Superior">Superior</option>
                  <option value="Pós-graduação">Pós-graduação</option>
                </select>
                {errors.nivel && (
                  <p className="mt-1 text-sm text-error-600">{errors.nivel.message}</p>
                )}
              </div>

              <div className="sm:col-span-2">
                <Label htmlFor="habilitacao">Habilitação</Label>
                <Input
                  id="habilitacao"
                  placeholder="Ex: Bacharelado, Licenciatura, etc."
                  {...register('habilitacao')}
                  className={errors.habilitacao ? 'border-error-500' : ''}
                />
                {errors.habilitacao && (
                  <p className="mt-1 text-sm text-error-600">{errors.habilitacao.message}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/cursos')}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting || loadingCurso}
          >
            {isSubmitting ? 'Salvando...' : (isEditing ? 'Atualizar Curso' : 'Criar Curso')}
          </Button>
        </div>
      </form>
    </div>
  );
};
